import { Editor, JSONContent } from '@tiptap/core';
import React, { FC } from 'react';
import { useGuideCmsContext } from '../../GuideCmsContext';
import { NumberedNodes, useNumberedNodes } from '../../tiptap/numbering';
import { REFERENCE_CANDIDATE_PATTERNS } from './referenceCandidatePatterns';
import { InternalLinkFormat } from '../../tiptap/extensions/plugins/InternalLink';

interface ReferencesContext {
  references: References;
  hasReferences: boolean;
  brokenReferences: References;
  hasBrokenReferences: boolean;
  missingReferences: MissingReferences;
  hasMissingReferences: boolean;
  candidates: ReferenceCandidates;
  hasCandidates: boolean;
  ieeeCitationLinks: JSONContent[];
  hasIeeeCitationLinks: boolean;
}

interface References {
  guideLinks: JSONContent[];
  standardLinks: JSONContent[];
  citationLinks: JSONContent[];
  internalHeadingLinks: JSONContent[];
  internalTableLinks: JSONContent[];
  internalFigureLinks: JSONContent[];
  internalAppLinks: JSONContent[];
}

interface MissingReferences {
  tables: JSONContent[];
  figures: JSONContent[];
  apps: JSONContent[];
}

interface ReferenceCandidates {
  headings: string[];
  figures: string[];
  tables: string[];
  guides: string[];
  standards: string[];
}

const ReferencesContextInstance = React.createContext<ReferencesContext | undefined>(undefined);

export const useReferencesContext = (): ReferencesContext => {
  const context = React.useContext(ReferencesContextInstance);
  if (!context) {
    throw new Error('useReferencesContext must be used within a ReferencesContextProvider');
  }
  return context;
};

export const ReferencesContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { editor, guide } = useGuideCmsContext();
  const numberedNodes = useNumberedNodes();
  const validCitationIds = new Set((guide?.citations ?? []).filter((c): c is { id: string; text: string } => !!c).map((c) => c.id));
  const context = createContext(editor, numberedNodes, validCitationIds);

  return <ReferencesContextInstance.Provider value={context}>{children}</ReferencesContextInstance.Provider>;
};

const createContext = (editor: Editor | null, numberedNodes: NumberedNodes, validCitationIds: Set<string>) => {
  if (!editor || editor.isDestroyed) {
    return {
      references: {
        guideLinks: [],
        standardLinks: [],
        citationLinks: [],
        internalHeadingLinks: [],
        internalTableLinks: [],
        internalFigureLinks: [],
        internalAppLinks: [],
      },
      hasReferences: false,
      brokenReferences: {
        guideLinks: [],
        standardLinks: [],
        citationLinks: [],
        internalHeadingLinks: [],
        internalTableLinks: [],
        internalFigureLinks: [],
        internalAppLinks: [],
      },
      hasBrokenReferences: false,
      missingReferences: { tables: [], figures: [], apps: [] },
      hasMissingReferences: false,
      candidates: { headings: [], figures: [], tables: [], guides: [], standards: [] },
      hasCandidates: false,
      ieeeCitationLinks: [],
      hasIeeeCitationLinks: false,
    };
  }
  const json = editor.getJSON();
  const { references, brokenReferences, ieeeCitationLinks } = getReferences(json, numberedNodes, validCitationIds);
  const missingReferences = getMissingReferences(json, references, numberedNodes);
  const candidates = getReferenceCandidates(json);
  return {
    references,
    hasReferences: hasReferences(references),
    brokenReferences,
    hasBrokenReferences: hasReferences(brokenReferences),
    missingReferences,
    hasMissingReferences: hasMissingReferences(missingReferences),
    candidates,
    hasCandidates: hasCandidates(candidates),
    ieeeCitationLinks,
    hasIeeeCitationLinks: ieeeCitationLinks.length > 0,
  };
};

const hasCandidates = (candidates: ReferenceCandidates) => {
  return (
    candidates.headings.length > 0 ||
    candidates.figures.length > 0 ||
    candidates.tables.length > 0 ||
    candidates.guides.length > 0 ||
    candidates.standards.length > 0
  );
};

const hasReferences = (references: References) => {
  return (
    references.guideLinks.length > 0 ||
    references.standardLinks.length > 0 ||
    references.citationLinks.length > 0 ||
    references.internalAppLinks.length > 0 ||
    references.internalFigureLinks.length > 0 ||
    references.internalHeadingLinks.length > 0 ||
    references.internalTableLinks.length > 0
  );
};

const hasMissingReferences = (missingReferences: MissingReferences) => {
  return missingReferences.tables.length > 0 || missingReferences.figures.length > 0 || missingReferences.apps.length > 0;
};

const getReferences = (root: JSONContent, numberedNodes: NumberedNodes, validCitationIds: Set<string>) => {
  const references: References = {
    guideLinks: [],
    standardLinks: [],
    citationLinks: [],
    internalHeadingLinks: [],
    internalTableLinks: [],
    internalFigureLinks: [],
    internalAppLinks: [],
  };

  const brokenReferences: References = {
    guideLinks: [],
    standardLinks: [],
    citationLinks: [],
    internalHeadingLinks: [],
    internalTableLinks: [],
    internalFigureLinks: [],
    internalAppLinks: [],
  };

  const ieeeCitationLinks: JSONContent[] = [];

  const findReferences = (node: JSONContent) => {
    if (node.type === 'internalLink' && node.attrs?.format === InternalLinkFormat.CitationIEEEStyle) {
      ieeeCitationLinks.push(node);
    }

    if (node.type === 'guideLink') {
      pushIfNotExists(node, references.guideLinks, (n) => n.attrs?.guideId);
    }
    if (node.type === 'standardLink') {
      pushIfNotExists(node, references.standardLinks, (n) => n.attrs?.standardId);
    }
    if (node.type === 'citationLink') {
      const citationId = node.attrs?.citationId;
      const target = citationId && validCitationIds.has(citationId) ? references.citationLinks : brokenReferences.citationLinks;
      pushIfNotExists(node, target, (n) => n.attrs?.citationId);
    }
    if (node.type === 'internalLink') {
      const referencedNode = numberedNodes.get(node.attrs?.id);
      if (node.attrs?.type === 'heading') {
        pushIfNotExists(node, referencedNode ? references.internalHeadingLinks : brokenReferences.internalHeadingLinks, (n) => n.attrs?.id);
      }
      if (node.attrs?.type === 'tableFigure') {
        pushIfNotExists(node, referencedNode ? references.internalTableLinks : brokenReferences.internalTableLinks, (n) => n.attrs?.id);
      }
      if (node.attrs?.type === 'imageFigure') {
        pushIfNotExists(node, referencedNode ? references.internalFigureLinks : brokenReferences.internalFigureLinks, (n) => n.attrs?.id);
      }
      if (node.attrs?.type === 'app') {
        pushIfNotExists(node, referencedNode ? references.internalAppLinks : brokenReferences.internalAppLinks, (n) => n.attrs?.id);
      }
    }

    if (node.content) {
      node.content?.forEach(findReferences);
    }
  };
  root.content?.forEach(findReferences);
  return { references, brokenReferences, ieeeCitationLinks };
};

const getMissingReferences = (root: JSONContent, references: References, numberedNodes: NumberedNodes): MissingReferences => {
  const missingReferences: MissingReferences = {
    tables: [],
    figures: [],
    apps: [],
  };

  const findMissingReferences = (node: JSONContent) => {
    if (node.attrs?.id) {
      const numbering = numberedNodes.get(node.attrs.id);
      if (numbering?.h1Level === 0) {
        // It is not required to reference nodes on the first level 0
        return;
      }
    }
    if (node.type === 'tableFigure' && references.internalTableLinks.every((n) => n.attrs?.id !== node.attrs?.id)) {
      pushIfNotExists(node, missingReferences.tables, (n) => n.attrs?.id);
    }
    if ((node.type === 'imageFigure' || node.type === 'chartFigure') && references.internalFigureLinks.every((n) => n.attrs?.id !== node.attrs?.id)) {
      pushIfNotExists(node, missingReferences.figures, (n) => n.attrs?.id);
    }
    if (node.type === 'app' && references.internalAppLinks.every((n) => n.attrs?.id !== node.attrs?.id)) {
      pushIfNotExists(node, missingReferences.apps, (n) => n.attrs?.id);
    }
    if (node.content) {
      node.content?.forEach(findMissingReferences);
    }
  };
  root.content?.forEach(findMissingReferences);
  return missingReferences;
};

const getReferenceCandidates = (root: JSONContent): ReferenceCandidates => {
  const candidates: ReferenceCandidates = {
    headings: [],
    figures: [],
    tables: [],
    guides: [],
    standards: [],
  };

  const findReferenceCandidates = (node: JSONContent) => {
    if (node.type === 'text' && node.text) {
      candidates.figures.push(...getFigureCandidates(node.text));
      candidates.tables.push(...getTableCandidates(node.text));
      candidates.headings.push(...getHeadingCandidates(node.text));
      candidates.guides.push(...getGuideCandidates(node.text));
      candidates.standards.push(...getStandardCandidates(node.text));
    }
    if (node.content) {
      node.content?.forEach(findReferenceCandidates);
    }
  };
  root.content?.forEach(findReferenceCandidates);

  // Remove duplicates
  return {
    headings: [...new Set(candidates.headings)],
    figures: [...new Set(candidates.figures)],
    tables: [...new Set(candidates.tables)],
    guides: [...new Set(candidates.guides)],
    standards: [...new Set(candidates.standards)],
  };
};

const getFigureCandidates = (text: string): string[] => {
  const pattern = REFERENCE_CANDIDATE_PATTERNS.figures[0];
  const matches = [...text.matchAll(pattern)];
  return matches.map((match) => match[0]);
};

const getTableCandidates = (text: string): string[] => {
  const pattern = REFERENCE_CANDIDATE_PATTERNS.tables[0];
  const matches = [...text.matchAll(pattern)];
  return matches.map((match) => match[0]);
};

const getHeadingCandidates = (text: string): string[] => {
  const allMatches: string[] = [];
  REFERENCE_CANDIDATE_PATTERNS.headings.forEach((pattern) => {
    const matches = [...text.matchAll(pattern)];
    allMatches.push(...matches.map((match) => match[0]));
  });
  return allMatches;
};

const getGuideCandidates = (text: string): string[] => {
  const allMatches: string[] = [];
  REFERENCE_CANDIDATE_PATTERNS.guides.forEach((pattern) => {
    const matches = [...text.matchAll(pattern)];
    allMatches.push(...matches.map((match) => match[0]));
  });
  return allMatches;
};

const getStandardCandidates = (text: string): string[] => {
  const allMatches: string[] = [];
  REFERENCE_CANDIDATE_PATTERNS.standards.forEach((pattern) => {
    const matches = [...text.matchAll(pattern)];
    allMatches.push(...matches.map((match) => match[0]));
  });
  return allMatches;
};

const pushIfNotExists = (node: JSONContent, list: JSONContent[], idSelector: (node: JSONContent) => unknown) => {
  if (!list.some((n) => idSelector(n) === idSelector(node))) {
    list.push(node);
  }
};
